import React from 'react'
import Box from '@material-ui/core/Box'
import IconButton from '@material-ui/core/IconButton'
import Typography from '@material-ui/core/Typography'
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined'
import EventAvailableOutlinedIcon from '@material-ui/icons/EventAvailableOutlined'
import { makeStyles } from '@material-ui/core/styles'
import CustomAvailabilityDestroyDialog from '../custom-availability/custom-availability-destroy-dialog'
import { formatOverrideDate, formatTimeRange } from './utils'

const useStyles = makeStyles(theme => ({
  list: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    overflow: 'hidden',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': { borderBottom: 'none' },
  },
  dot: {
    width: 12,
    height: 12,
    borderRadius: 3,
    backgroundColor: '#FFFFFF',
    backgroundImage:
      'repeating-linear-gradient(135deg, #E3F2FD 0 4px, #FFFFFF 4px 8px)',
    border: '1px solid #90CAF9',
    boxSizing: 'border-box',
    display: 'inline-block',
    flexShrink: 0,
  },
  date: {
    fontWeight: 500,
  },
  meta: {
    color: theme.palette.text.secondary,
    fontVariantNumeric: 'tabular-nums',
    marginLeft: 'auto',
    textAlign: 'right',
  },
  facility: {
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
  },
  actions: {
    marginLeft: theme.spacing(1),
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: theme.spacing(5, 2),
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: 8,
    [theme.breakpoints.only('xs')]: {
      padding: theme.spacing(4, 2),
    },
  },
  emptyIcon: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#E3F2FD',
    color: '#1565C0',
    marginBottom: theme.spacing(1.5),
  },
  emptyTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
  },
  emptyCaption: {
    color: theme.palette.text.secondary,
    maxWidth: 480,
    marginBottom: theme.spacing(2),
  },
}))

const OverridesList = ({ overrides, onChanged, emptyAction }) => {
  const classes = useStyles()
  if (!overrides || overrides.length === 0) {
    return (
      <Box className={classes.empty}>
        <Box className={classes.emptyIcon}>
          <EventAvailableOutlinedIcon style={{ fontSize: 28 }} />
        </Box>
        <Typography variant="subtitle1" className={classes.emptyTitle}>
          No Open Time
        </Typography>
        <Typography variant="body2" className={classes.emptyCaption}>
          Add extra bookable hours on a specific day, outside your recurring
          weekly schedule.
        </Typography>
        {emptyAction}
      </Box>
    )
  }
  return (
    <Box className={classes.list}>
      {overrides.map(override => {
        const sortedSlots = [...(override.timeSlots || [])].sort((a, b) =>
          a.startTime < b.startTime ? -1 : a.startTime > b.startTime ? 1 : 0,
        )
        const timeRanges = sortedSlots
          .map(ts => formatTimeRange(ts.startTime, ts.endTime))
          .join(', ')
        const facility = override.coachFacility?.name
        return (
          <Box key={override.id} className={classes.row}>
            <span className={classes.dot} aria-hidden />
            <Box>
              <Typography variant="body2" className={classes.date}>
                {formatOverrideDate(override.date)}
              </Typography>
              {facility && (
                <Typography variant="caption" className={classes.facility}>
                  {facility}
                </Typography>
              )}
            </Box>
            <Typography variant="body2" className={classes.meta}>
              {timeRanges}
            </Typography>
            <Box className={classes.actions}>
              <CustomAvailabilityDestroyDialog
                override={override}
                onSuccess={onChanged}
                trigger={
                  <IconButton size="small" aria-label="delete override">
                    <DeleteOutlinedIcon fontSize="small" />
                  </IconButton>
                }
              />
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

export default OverridesList
